import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import QueryString from "query-string";
import { Button } from "react-bootstrap";
import utils from "@utils";
import {
	ContactService,
	OrganizationService,
	ClientService,
	ProjectService,
	LocationService,
	ReportService,
	PayCodesService,
} from "@services";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { USER_TYPE, REPORTS_TAB_MENU } from "@constants";
import { ANALYTICS_EVENTS_ACTIONS, ANALYTICS_EVENTS_CATEGORIES } from "@constants/analyticsEvents";
import AnalyticsService from "@services/AnalyticsService";
import { SideBar, Report, ReportGrid, ReportSidebar } from "./components";
import { TabMenu, Loader } from "../../../components";
import "./report.scss";

class Reports extends Component {
	constructor(props) {
		super(props);

		const today = new Date(new Date().setHours(0, 0, 0, 0));
		const yesterday = new Date(
			new Date(new Date().setHours(0, 0, 0, 0)).setDate(today.getDate() - 1),
		);

		const {
			user: {
				Organization: { PayCodesEnabled, Id: OrganizationId },
			},
			flags: { showAgencyReportOverride },
		} = this.props;

		this.initialFilters = {
			startDateRange: utils.formatDate(
				yesterday,
				utils.isDesktopSafari || utils.isIE ? "en-US" : undefined,
			),
			endDateRange: utils.formatDate(
				today,
				utils.isDesktopSafari || utils.isIE ? "en-US" : undefined,
			),
			agencyId: "",
			clientId: "",
			projectId: "",
			locationId: "",
			clientSupervisorContactId: "",
			officer: { id: "", name: "" },
			jurisdictionId: "",
			includeAll: false,
			payCodeName: "",
			isPayCodeEnabled: PayCodesEnabled || false,
			organizationId: OrganizationId || "",
		};

		this.initialState = {
			reportType: "",
			reportSource: "",
			reportId: "",
			renderReport: false,
			agencies: [],
			clients: [],
			projects: [],
			locations: [],
			contacts: [],
			paycodes: [],
			reports: [],
			filters: { ...this.initialFilters },
			reportParams: {},
			activeSubTab: "legacy",
			loaders: {
				agencies: false,
				clients: false,
				projects: false,
				locations: false,
				contacts: false,
				paycodes: false,
			},
			sidebarFixed: false,
            domoLoading: false,
		};

		this.showAgencyReportOverride = !!showAgencyReportOverride;
		this.state = { ...this.initialState };
	}

	componentDidMount() {
		const {
			user: {
				UserTypeId,
				Organization: { Id: orgId, JurisdictionId },
			},
			location
		} = this.props;
		const parsed = QueryString.parse(location.search);
		this.setState({ hideDomoReport: true, showFilters: true, activeSubTab: "insights" });

		if (parsed.reportType && parsed.id && parsed.property) {
			this.setState(prevState => ({
				reportType: parsed.reportType,
				filters: {
					...prevState.filtes,
					[parsed.property]: parseInt(parsed.id, 10),
				},
			}));

			setTimeout(() => {
				this.generateReport();
			}, 0);
		}

		// Currently CLIENT users should not login on Agency Portal,
		// reports and other features are not fully supported
		if (UserTypeId === USER_TYPE.CLIENT) {
			this.updateFilters("clientId", orgId);
			this.getContacts(orgId);
		} else if (UserTypeId === USER_TYPE.SUPER_ADMIN) {
			this.getAgencies();
		} else if (UserTypeId === USER_TYPE.LAW_ENFORCEMENT) {
			this.updateFilters("jurisdictionId", JurisdictionId);
			this.getClients();
		} else {
			this.updateFilters("agencyId", orgId);
			this.getClients();
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				const { renderReport } = this.state;
				this.setState({ sidebarFixed: renderReport && !entry.isIntersecting });
			},
			{ root: null, rootMargin: "0px", threshold: 0 },
		);
		observer.observe(document.getElementById("app-navbar"));
	}

	componentWillUnmount() {
		this.setState({ ...this.initialState });
		document.querySelector("body").style.overflow = "initial";
	}

	resetFilters = (callback = () => {}) => {
		const {
			user: { UserTypeId },
		} = this.props;
		const newState = { ...this.initialState };
		if (!UserTypeId) {
			this.setState(newState, callback);
			return;
		}

		this.setState(prevState => {
			if (UserTypeId === USER_TYPE.SUPER_ADMIN) {
				newState.filters.agencyId = prevState.filters.agencyId;
				newState.agencies = prevState.agencies;
			} else if (UserTypeId === USER_TYPE.CLIENT)
				newState.filters.clientId = prevState.filters.clientId;
			else if (UserTypeId === USER_TYPE.LAW_ENFORCEMENT) {
				newState.clients = prevState.clients;
				newState.filters.jurisdictionId = prevState.filters.jurisdictionId;
			} else {
				newState.filters.agencyId = prevState.filters.agencyId;
				newState.clients = prevState.clients;
			}

			if (this.showAgencyReportOverride && UserTypeId !== USER_TYPE.SUPER_ADMIN) {
				newState.filters.agencyId = prevState.filters.agencyId;
			}
			newState.activeSubTab = prevState.activeSubTab;

			return newState;
		}, callback);
	};

	updateFilters = (property, value) => {
		this.setState(prevState => ({
			filters: {
				...prevState.filters,
				[property]: value,
			},
		}));
	};

	onClickTab = async ({ tabNumber }) => {
		const newSubTab = REPORTS_TAB_MENU.find(tab => tab.tabNumber === tabNumber).type;
		const { activeSubTab } = this.state;
		// Sets scroll back to initial in case a Insights report was clicked
		document.querySelector("body").style.overflow = "initial";

		if (newSubTab !== activeSubTab) {
			this.resetFilters();
			this.setState({ activeSubTab: newSubTab, hideDomoReport: true, showFilters: true });
		}
	};

	getContacts = clientId => {
		const { lang } = global;
		this.setState(prevState => ({ loaders: { ...prevState.loaders, contacts: true } }));

		ContactService.getContactsByClientId(clientId, { minimalData: true })
			.then(({ data }) => {
				const contacts = data.ItemList.map(({ Id: value, ContactName: label }) => ({
					value,
					label,
				}));
				this.setState(prevState => ({
					contacts,
					loaders: { ...prevState.loaders, contacts: false },
				}));
			})
			.catch(() => {
				this.setState(prevState => ({
					loaders: { ...prevState.loaders, contacts: false },
				}));
				utils.notify({
					type: "error",
					message: lang.t("Error loading contacts"),
					timeOut: 1500,
				});
			});
	};

	getPayCodes = () => {
		PayCodesService.getPaycodes()
			.then(({ data }) => {
				const _agencyPayCodes = data.map(el => ({
					paycodeId: el.Id,
					payCodeName: el.Name,
					value: el.Name,
					label: `${el.Name}, ${el.Description}`,
				}));

				this.setState({ paycodes: _agencyPayCodes });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Error loading Pay Codes",
					timeOut: 1500,
				});
			});
	};

	getAgencies = () => {
		const { lang } = global;
		this.setState(prevState => ({ loaders: { ...prevState.loaders, agencies: true } }));

		OrganizationService.getAll({ minimalData: true })
			.then(({ data }) => {
				const agencies = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState(prevState => ({
					agencies,
					loaders: { ...prevState.loaders, agencies: false },
				}));
			})
			.catch(() => {
				this.setState(prevState => ({
					loaders: { ...prevState.loaders, agencies: false },
				}));
				utils.notify({
					type: "error",
					message: lang.t("Error loading Agencies"),
					timeOut: 1500,
				});
			});
	};

	getClients = (organizationId = null, includeInactive = false) => {
		const { lang } = global;
		this.setState(prevState => ({ loaders: { ...prevState.loaders, clients: true } }));

		ClientService.getAll({ organizationId, includeInactive })
			.then(({ data }) => {
				const clients = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState(prevState => ({
					clients,
					loaders: { ...prevState.loaders, clients: false },
				}));
			})
			.catch(() => {
				this.setState(prevState => ({ loaders: { ...prevState.loaders, clients: false } }));
				utils.notify({
					type: "error",
					message: lang.t("Error loading clients"),
					timeOut: 1500,
				});
			});
	};

	getProjects = (clientId, includeAll = false) => {
		const { lang } = global;
		const {
			user: { UserTypeId },
		} = this.props;

		this.setState(prevState => ({ loaders: { ...prevState.loaders, projects: true } }));
		/** If the current user is COORDINATOR, the locations loader should start loading inmedialty */
		// if (UserTypeId === USER_TYPE.COORDINATOR) dispatch(Actions.updateLoaders('locations', true));

		ProjectService.getAll({ clientId, includeAll, minimalData: true })
			.then(({ data }) => {
				const projects = data.ItemList.map(({ Id: value, Name: label }) => ({
					value,
					label,
				}));
				this.setState(prevState => ({
					projects,
					loaders: { ...prevState.loaders, projects: false },
				}));

				/** If the current user is COORDINATOR, the project will be set automatically with the first option. */
				if (UserTypeId === USER_TYPE.COORDINATOR && projects.length === 1) {
					const [{ value: projectId }] = projects;
					this.setState(prevState => ({ filters: { ...prevState.filters, projectId } }));
					this.getLocations(projectId);
				}
			})
			.catch(() => {
				this.setState(prevState => ({
					loaders: { ...prevState.loaders, projects: false },
				}));
				utils.notify({
					type: "error",
					message: lang.t("Error loading projects"),
					timeOut: 1500,
				});
			});
	};

	getLocations = projectId => {
		const { lang } = global;
		this.setState(prevState => ({ loaders: { ...prevState.loaders, locations: true } }));

		LocationService.getAll({ projectId, minimalData: true })
			.then(({ data }) => {
				const locations = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState(prevState => ({
					locations,
					loaders: { ...prevState.loaders, locations: false },
				}));
			})
			.catch(() => {
				this.setState(prevState => ({
					loaders: { ...prevState.loaders, locations: false },
				}));
				utils.notify({
					type: "error",
					message: lang.t("Error loading Locations"),
					timeOut: 1500,
				});
			});
	};

	resetReportParams = () => {
		setTimeout(() => {
			this.setState({
				reportParams: {},
			});
		}, 0);
	};

	restoreReportGrid = () => {
		setTimeout(() => {
			this.setState({ hideDomoReport: true, showFilters: true });
		}, 0);
	};

	validateFilters = reportType => {
		const { filters } = this.state;
		const { user } = this.props;

		switch (reportType) {
			case "client_overview": {
				if (filters.clientId === "") {
					this._report.setValidationMessage(
						"You need to select a Client to generate the report.",
					);
					return false;
				}
				break;
			}
			case "project_overview": {
				if (filters.projectId === "") {
					this._report.setValidationMessage(
						"You need to select a Project to generate the report.",
					);
					return false;
				}
				break;
			}
			case "officer_overview": {
				if (filters.officer.id === "") {
					this._report.setValidationMessage(
						"You need to select an Officer to generate the report.",
					);
					return false;
				}
				break;
			}
			case "location_overview": {
				if (filters.locationId === "") {
					this._report.setValidationMessage(
						"You need to select a Location to generate the report.",
					);
					return false;
				}
				break;
			}
			case "jurisdiction_overview": {
				if (filters.jurisdictionId === "") {
					this._report.setValidationMessage(
						"You need to select a Jurisdiction to generate the report.",
					);
					return false;
				}
				break;
			}
			case "agency_overview": {
				if (user.isSuperAdmin() && filters.agencyId === "") {
					this._report.setValidationMessage(
						"You need to select an Agency to generate the report.",
					);
					return false;
				}
				break;
			}
			case "fairness": {
				if (user.isSuperAdmin() && filters.jurisdictionId === "") {
					this._report.setValidationMessage(
						"You need to select a Jurisdiction to generate the report.",
					);
					return false;
				}
				break;
			}
			default:
				return true;
		}

		return true;
	};

	generateReport = async () => {
		const { lang } = global;
		const { reportType, filters, reportSource, reportId } = this.state;

		const cleanFilters = {
			...filters,
			startDateRange: utils.fixIELocaleString(filters.startDateRange),
			endDateRange: utils.fixIELocaleString(filters.endDateRange),
		};
		if (reportSource === "domo") {
			const report = await ReportService.getEmbeddedReportById(reportId, filters.agencyId);
            this.setState({domoLoading: true})
            setTimeout(() => this.setState({ domoLoading: false }), 4000)
			this._report.setLoading(true,() => {
					this.setState(prevState => ({
						renderReport: true,
						showFilters: prevState.showFilters,
						reportParams: { reportLink: report.data.Message },
						hideDomoReport: false,
					}));
				},
			);
			document.querySelector("body").style.overflow = "hidden";
            this._report.setLoading(false);
		} else if (reportType !== "" && this.validateFilters(reportType)) {
			const report = ReportService.getReportSettingByType(reportType);
			AnalyticsService.trackEvent({
				category: ANALYTICS_EVENTS_CATEGORIES.REPORT_REQUEST,
				action: ANALYTICS_EVENTS_ACTIONS.REPORT_REQUESTED,
				customParameters: {
					reportType,
					reportName: report.name,
				},
			});
			this._report.setLoading(true, () => {
				this.setState(prevState => ({
					renderReport: true,
					showFilters:
						window.innerWidth < 768 ? !prevState.showFilters : prevState.showFilters,
				}));

				// Generate the needed report
				ReportService[reportType](cleanFilters, reportType)
					.then(response => {
						const rawData = response.data;
						if (reportType !== "invoice_aging" && reportType !== "payroll_report") {
							rawData.JobOverviewList = rawData.JobOverviewList || [];
							rawData.LocationJobOverviewList = rawData.LocationJobOverviewList || [];
							rawData.OfficerJobOverviewList = rawData.OfficerJobOverviewList || [];
							rawData.ProjectJobOverviewList = rawData.ProjectJobOverviewList || [];
							rawData.ClientJobOverviewList = rawData.ClientJobOverviewList || [];
						}

						this.setState(
							{
								reportParams:
									reportType === "invoice_aging" ||
									reportType === "payroll_report"
										? { data: rawData }
										: rawData,
							},
							() => {
								this._report.setLoading(false);
							},
						);
					})
					.catch(() => {
						utils.notify({
							type: "error",
							message: lang.t("Error generating report"),
							title: "Oops",
						});
					});
			});
		}
	};

	generateManualInvoiceReport = (reportType, queryParams) => {
		if (reportType !== "" && this.validateFilters(reportType)) {
			const report = ReportService.getReportSettingByType(reportType);
			AnalyticsService.trackEvent({
				category: ANALYTICS_EVENTS_CATEGORIES.REPORT_REQUEST,
				action: ANALYTICS_EVENTS_ACTIONS.REPORT_REQUESTED,
				customParameters: {
					reportType,
					reportName: report.name,
				},
			});
			this._report.setLoading(true, () => {
				this.setState({ renderReport: true });

				ReportService[reportType](reportType, queryParams)
					.then(({ data }) =>
						this.setState(
							{
								reportParams: { data },
							},
							() => this._report.setLoading(false),
						),
					)
					.catch(() => {});
			});
		}
	};

	updateState = setState => this.setState(setState);

	render() {
		const { lang } = global;
		const {
			reportType,
			reportId,
			reportSource,
			renderReport,
			reportParams,
			agencies,
			reports,
			clients,
			projects,
			locations,
			contacts,
			filters,
			loaders,
			showFilters,
			sidebarFixed,
			paycodes,
			activeSubTab,
			hideDomoReport,
            domoLoading
		} = this.state;
		const { user } = this.props;

		const reportTabs = REPORTS_TAB_MENU.map(item => {
			if (item.type === "legacy") return { ...item, text: lang.t("Legacy") };
            if (item.type === 'insights' && user.Organization.EnhancedReportingTier === 1) return { ...item, text: lang.t("Standard") };
			return { ...item, text: lang.t("Insights") };
		});

		return (
			<Fragment>
                <div>
                    <TabMenu
                        tabs={reportTabs}
                        activeTab={reportTabs.find(tab => tab.type === activeSubTab).tabNumber}
                        onClickTab={this.onClickTab}
                    />
                </div>
				<If
					condition={
						activeSubTab === REPORTS_TAB_MENU.find(tab => tab.tabNumber === 0).type
					}
				>
					<div className="flex flex-align-items-top">
						<div
							className={`${hideDomoReport ? "domo-reports-container-hide" : "domo-reports-sidebar-container"} 'fixed'
                            ${showFilters ? "margin-left-0" : "margin-left-small-icons"}`}
						>
							<Button
								onClick={() =>
									this.setState(prevState => ({
										showFilters: !prevState.showFilters,
									}))
								}
								bsStyle="primary"
								id="btn-collapse-domo"
							>
								<i
									className={`rkf ${showFilters ? "rkf-chevron-left" : "rkf-chevron-right"}`}
								/>
							</Button>
							<div
								className={`${hideDomoReport ? "domo-reports-container-hide" : "width-260"}`}
							>
								<ReportSidebar
									generateReportBtnCallback={this.generateReport}
									resetReportParams={this.resetReportParams}
									reports={reports}
									reportType={reportType}
									reportSource={reportSource}
									reportId={reportId}
									loaders={loaders}
									setReports={this.updateState}
									setReportState={this.updateState}
									smallIcons={!showFilters}
									resetFilters={this.resetFilters}
									restoreReportGrid={this.restoreReportGrid}
								/>
							</div>
						</div>
						<div
							className={`${hideDomoReport ? "agency-grid-show" : "domo-reports-container-hide"}`}
						>
							<ReportGrid
								generateReportBtnCallback={this.generateReport}
								resetReportParams={this.resetReportParams}
								user={user}
								reportType={reportType}
								reportSource={reportSource}
								reportId={reportId}
								agencies={agencies}
								setReports={this.updateState}
								filters={filters}
								loaders={loaders}
								setReportState={this.updateState}
								updateFilters={this.updateFilters}
								resetFilters={this.resetFilters}
								showAgencyReportOverride={this.showAgencyReportOverride}
							/>
						</div>
						<div
							className={`
                            ${hideDomoReport ? "domo-reports-container-hide" : "domo-reports-container-show"}`}
							id="domo-reports-container"
						>
							<Report
								user={user}
								ref={ref => {
									this._report = ref;
								}}
								type={reportType}
								filters={filters}
								params={reportParams}
								renderReport={renderReport}
								timeZoneId={user.TimeZoneId}
                                source="domo"
							/>
                            <Loader active={domoLoading} />
						</div>
					</div>
				</If>
				<If
					condition={
						activeSubTab === REPORTS_TAB_MENU.find(tab => tab.tabNumber === 1).type
					}
				>
					<div className="flex flex-align-items-top">
						<div
							className={`reports-sidebar-container ${sidebarFixed ? "fixed" : ""} ${
								showFilters ? "margin-left-0" : "margin-left-minus-280"
							}`}
						>
							<Button
								onClick={() =>
									this.setState(prevState => ({
										showFilters: !prevState.showFilters,
									}))
								}
								bsStyle="primary"
								id="btn-collapse"
							>
								<i
									className={`rkf ${showFilters ? "rkf-chevron-left" : "rkf-chevron-right"}`}
								/>
							</Button>
							<div className="width-260">
								<SideBar
									generateReportBtnCallback={this.generateReport}
									generateManualInvoiceCallback={this.generateManualInvoiceReport}
									resetReportParams={this.resetReportParams}
									user={user}
									reportType={reportType}
									reportSource={reportSource}
									reportId={reportId}
									agencies={agencies}
									clients={clients}
									projects={projects}
									locations={locations}
									contacts={contacts}
									filters={filters}
									loaders={loaders}
									setReportState={this.updateState}
									updateFilters={this.updateFilters}
									resetFilters={this.resetFilters}
									getClients={this.getClients}
									getProjects={this.getProjects}
									getContacts={this.getContacts}
									getLocations={this.getLocations}
									getPaycodes={this.getPayCodes}
									paycodes={paycodes}
								/>
							</div>
						</div>
						<div
							className={`${showFilters ? "show-filters" : ""} ${sidebarFixed && showFilters ? "fixed-sidebar-offset" : ""}`}
							id="reports-container"
						>
							<Report
								user={user}
								ref={ref => {
									this._report = ref;
								}}
								type={reportType}
								filters={filters}
								params={reportParams}
								renderReport={renderReport}
								timeZoneId={user.TimeZoneId}
                                source="rollkall"
							/>
						</div>
					</div>
				</If>
			</Fragment>
		);
	}
}

Reports.propTypes = {
	location: PropTypes.object.isRequired,
	user: PropTypes.object,
	flags: PropTypes.object.isRequired,
};

Reports.defaultProps = {
	user: {},
};

export default withLDConsumer()(Reports);
